/**
 * Awaited chunked `.in()` read for PostgREST queries (#2669).
 *
 * The chunk size and the splitting itself are the canonical ones from
 * `@/utils/arrays/chunkList` (#1944/#1951) — this module adds only the piece
 * that was being hand-rolled at every call site: run the query once per chunk,
 * concatenate the rows, and fail the whole read on any chunk error.
 *
 * Why it exists: PostgREST filters travel in the URL and the gateway rejects a
 * request line past ~8 KB with `414 Request-URI Too Large`. A UUID costs ~39
 * encoded chars inside `in.(…)`, so the ceiling lands somewhere around 200
 * ids depending on how fat the `select=` clause is — the staff Athletes list
 * died at 213. The failure scales with the customer's roster, so it only ever
 * hits the biggest gyms, and never the seed data anyone develops against.
 *
 * Prefer a server-side join when one exists (e.g. filter athletes by
 * `athlete_organizations!inner(organization_id)` instead of collecting ids
 * first) — no id list in the URL at all, and one fewer round trip. Reach for
 * this helper when the ids are genuinely computed client-side and cannot be
 * expressed as a filter.
 *
 * Contract for `buildChunk(chunk)`:
 *   - Build a FRESH query each call — a PostgREST query builder cannot be
 *     re-filtered and re-awaited.
 *   - Apply `.in(<column>, chunk)` using the chunk it is handed, not the
 *     original array.
 *   - Return the awaited-able supabase result (`{ data, error }`).
 *
 * Errors fail the WHOLE read (empty rows + the error), matching
 * `fetchAllPages`: a partial roster silently rendered as the complete one is
 * worse than an honest failure.
 *
 * Note this caps only the URL, not the response. A chunk that can return more
 * than PostgREST's 1,000-row cap still needs `fetchAllPages` inside
 * `buildChunk`.
 */

import { chunkList, IN_LIST_CHUNK_SIZE } from '@/utils/arrays/chunkList';
import { fetchAllPages, type PageResult } from '@/utils/supabase/fetchAllPages';

export interface ChunkResult<T> {
  data: T[] | null;
  error: { message: string } | null;
}

export async function fetchInChunks<T>(
  ids: readonly string[],
  buildChunk: (chunk: string[]) => PromiseLike<ChunkResult<T>>,
  chunkSize: number = IN_LIST_CHUNK_SIZE,
): Promise<{ rows: T[]; error: Error | null }> {
  // Duplicates and blanks would inflate the URL back toward the limit for no
  // extra rows, so drop them before measuring anything.
  const unique = Array.from(new Set(ids.filter(Boolean)));
  if (unique.length === 0) return { rows: [], error: null };

  let chunks: string[][];
  try {
    chunks = chunkList(unique, chunkSize);
  } catch (err: any) {
    return { rows: [], error: err instanceof Error ? err : new Error(String(err)) };
  }

  const rows: T[] = [];
  // Sequential, not Promise.all: a 2,000-athlete roster would otherwise fire 20
  // simultaneous requests, and the browser's per-host connection queue makes
  // that no faster — just burstier on the database.
  for (const chunk of chunks) {
    const { data, error } = await buildChunk(chunk);
    if (error) {
      return { rows: [], error: new Error(error.message) };
    }
    rows.push(...(data ?? []));
  }
  return { rows, error: null };
}

/**
 * Both ceilings at once: chunk the id list to stay under the URL limit AND page
 * each chunk to stay under PostgREST's 1,000-row response cap.
 *
 * Chunking alone is not enough whenever a row is NOT one-per-athlete. A chunk
 * of 100 athletes pulling their completed lessons, ended enrollments, or status
 * history can easily exceed 1,000 rows, and PostgREST truncates that silently —
 * the read succeeds and simply returns less history, so an athlete's status
 * quietly computes from incomplete data. Use this for any per-athlete-history
 * read; plain `fetchInChunks` is fine only when the response is bounded by the
 * chunk size (one row per athlete).
 *
 * `buildPage(chunk, from, to)` must build a FRESH query ending in
 * `.order(<stable column>, { ascending: true }).range(from, to)` — usually `id`.
 * That paging order overrides any display order, so sort the merged rows
 * yourself afterwards if order matters.
 */
export async function fetchAllInChunks<T>(
  ids: readonly string[],
  buildPage: (chunk: string[], from: number, to: number) => PromiseLike<PageResult<T>>,
  chunkSize: number = IN_LIST_CHUNK_SIZE,
): Promise<{ rows: T[]; error: Error | null }> {
  return fetchInChunks<T>(
    ids,
    (chunk) =>
      fetchAllPages<T>((from, to) => buildPage(chunk, from, to)).then(({ rows, error }) => ({
        data: rows,
        error: error ? { message: error.message } : null,
      })),
    chunkSize,
  );
}
